import React from 'react';
import { Box, ColumnLayout, IconProps, StatusIndicator } from '@amzn/awsui-components-core';
import {
  Autotag,
  ContentType,
  ContentTypeMetadata,
  TechDomainDetails,
} from '@amzn/awscat-pattern-library-service-model';
import pluralize from 'pluralize';
import { ContentUIComponent } from '../../model/Enum';
import {
  MetadataKeyOptionsMap,
  MetadataKeyReadonlyOptionsMap,
  resolveWorkloadSelection,
} from '../utils/SelectableOptions';
import MetadataCheckboxInput from '../workflow-viewer/content/MetadataCheckboxInput';
import MetadataMultiselectInputV3 from '../workflow-viewer/content/MetadataMultiselectInputV3';
import MetadataSelectInput from '../workflow-viewer/content/MetadataSelectInput';
import MetadataStringInput from '../workflow-viewer/content/MetadataStringInput';
import constants from '../../common/constants';
import ContentTypeMetadataForm from './ContentTypeMetadataForm';
import {
  autotagMetadataAttributes,
  metadataToAutotagDisplayMap,
  metadataToAutotagTypeMap,
} from '../utils/CreateEditContentUtils';

interface Props {
  contentType?: ContentType;
  contentTypeMetadata?: ContentTypeMetadata;
  disabled?: boolean;
  onContentMetadataUpdated?: (sectionId: string, value: any, validationError?: string) => void;
  onContentTypeMetadataUpdated?: (sectionId: string, value: any, validationError?: string) => void;
  onTemplateMetadataUpdated?: (sectionId: string, value: any, validationError?: string) => void;
  onValidationError?: (label: string) => (validationError?: string) => void;
  readonly?: boolean;
  showAutotagTemplate?: boolean;
  showValidationError?: boolean;
  autotags?: Autotag[];
  templateData?: any;
  templateSchema: any;
  visible?: boolean;
}

// This component is a combination of ContentTypeMetadataForm and TemplateMetadataForm and is created to support Polaris V3 ColumnLayout.
// There is duplication of TemplateMetadataForm code in this compnent.
// TODO --- TemplateMetadataForm can be deprecated once all components start using ContentMetadataForm

// Module-level so the default keeps a stable identity across renders, as defaultProps did.
const NO_TEMPLATE_DATA = {};

const ContentMetadataForm: React.FunctionComponent<Props> = (props: Props) => {
  const {
    autotags,
    contentType,
    contentTypeMetadata,
    disabled = false,
    readonly = false,
    onContentMetadataUpdated,
    onContentTypeMetadataUpdated,
    onTemplateMetadataUpdated,
    onValidationError,
    showAutotagTemplate,
    showValidationError = true,
    templateData = NO_TEMPLATE_DATA,
    templateSchema,
    visible = false,
  } = props;

  const renderContentTypeMetadata = () => {
    if (!contentTypeMetadata || contentType !== ContentType.Pattern) {
      return;
    }
    return (
      <ContentTypeMetadataForm
        contentType={contentType}
        contentTypeMetadata={contentTypeMetadata}
        visible={visible}
        readonly={readonly}
        onInputUpdated={onContentTypeMetadataUpdated}
      />
    );
  };

  const renderTemplateMetadata = () => {
    if (!visible || !templateSchema) {
      return null;
    }
    let metaAttributes = Object.keys(templateSchema.properties);
    if (showAutotagTemplate) {
      metaAttributes = metaAttributes.filter((metaAttribute) =>
        autotagMetadataAttributes.has(metaAttribute),
      );
    }
    const templateForm = metaAttributes.map((key) => {
      const prop = templateSchema.properties[key];
      if (!prop) {
        return null;
      }
      const isRequired: boolean = !!(
        Array.from(templateSchema.required) && templateSchema.required.includes(key)
      );
      const autotagType = metadataToAutotagTypeMap.get(key);
      const tags =
        (autotagType && autotags?.find((autotag) => autotag.type === autotagType)?.tags) || [];
      return (
        <div key={key}>
          {renderUIComponent(key, prop, isRequired, tags)}
          {renderAutotagDetail(key, tags)}
        </div>
      );
    });
    return templateForm;
  };

  // @ts-expect-error TS7030 - strict migration
  const renderAutotagDetail = (key: string, tags: string[]) => {
    const displayName = metadataToAutotagDisplayMap.get(key);
    const filteredTags = tags.filter((tag) => templateData[key]?.includes(tag));
    if (displayName && filteredTags.length) {
      const label = pluralize(displayName, filteredTags.length, true);
      return (
        <Box float="left">
          <StatusIndicator> We've added {label}</StatusIndicator>
        </Box>
      );
    }
  };
  // @ts-expect-error TS7030 - strict migration
  const renderUIComponent = (
    key: string,
    jsonSchemaProp: any,
    isRequired: boolean = false,
    tags?: string[],
  ) => {
    const { UIComponent, label, description, contextualHelp, maxNumber } = jsonSchemaProp;
    const metadataOptionMap = readonly ? MetadataKeyReadonlyOptionsMap : MetadataKeyOptionsMap;
    switch (UIComponent) {
      case ContentUIComponent.MetadataStringInput:
        return (
          <MetadataStringInput
            sectionId={key}
            value={templateData[key]}
            label={label}
            description={description}
            contextualHelp={contextualHelp}
            placeholder={`Enter ${label}`}
            onInputUpdated={onTemplateMetadataUpdated}
            onValidationError={onValidationError && onValidationError(label)}
            readonly={readonly}
            isRequired={isRequired}
            stretch
            showValidationError={showValidationError}
            disabled={disabled}
            visible={visible}
          />
        );
      case ContentUIComponent.MetadataSelectInput:
        const singleSelectDetailsMap = new Map<string, any>([
          ['mainTechDomain', TechDomainDetails],
        ]);
        return (
          <MetadataSelectInput
            sectionId={key}
            label={label}
            description={description}
            contextualHelp={contextualHelp}
            readonly={readonly}
            isRequired={isRequired}
            disabled={disabled}
            showValidationError={showValidationError}
            selectedId={templateData[key]}
            selectOptions={metadataOptionMap.get(key) || []}
            onSelectUpdated={onTemplateMetadataUpdated}
            onValidationError={onValidationError && onValidationError(label)}
            stretch
            placeholder={`Choose ${label}`}
            visible={visible}
            detailsMap={singleSelectDetailsMap.get(key)}
          />
        );
      case ContentUIComponent.MetadataMultiselectInput:
        const selectedOptions = (metadataOptionMap.get(key) || [])
          // remove disabled options from selected list as they cannot be unselected
          .filter((option) => templateData[key]?.includes(option.value) && !option.disabled)
          .map((option) =>
            tags?.includes(option.value ?? '')
              ? { ...option, iconName: 'status-positive' as IconProps.Name }
              : { ...option, iconName: null },
          );
        const multiselectHandler =
          key === 'workload'
            ? (sectionId: string, values: string[], validationError?: string) => {
                const resolved = resolveWorkloadSelection(values, templateData[key] || []);
                onTemplateMetadataUpdated?.(sectionId, resolved, validationError);
              }
            : onTemplateMetadataUpdated;
        return (
          <MetadataMultiselectInputV3
            sectionId={key}
            label={label}
            description={description}
            contextualHelp={contextualHelp}
            readonly={readonly}
            isRequired={isRequired}
            disabled={disabled}
            showValidationError={showValidationError}
            // @ts-expect-error TS2322 - strict migration
            selectedOptions={selectedOptions}
            selectOptions={metadataOptionMap.get(key) || []}
            onSelectsUpdated={multiselectHandler}
            onValidationError={onValidationError && onValidationError(label)}
            stretch
            placeholder={`Choose ${label}`}
            maxNumber={maxNumber}
          />
        );
      case ContentUIComponent.MetadataCheckboxInput:
        return (
          <MetadataCheckboxInput
            sectionId={key}
            label={label}
            description={description}
            contextualHelp={contextualHelp}
            isChecked={!!templateData[key]}
            onChange={onTemplateMetadataUpdated}
            readonly={readonly}
            disabled={disabled}
          />
        );
    }
  };

  const renderTags = () => {
    const { tags } = templateData;
    if (!tags) {
      return;
    }
    return (
      <MetadataStringInput
        sectionId="tags"
        value={Array.isArray(tags) ? tags.join(', ') : ''}
        label={constants.TAGS_LABEL}
        placeholder={constants.TAGS_PLACEHOLDER}
        onInputUpdated={onContentMetadataUpdated}
        readonly={false}
        visible={false}
      />
    );
  };

  const contentTypeMetadataForm = renderContentTypeMetadata();
  const templateMetadataForm = renderTemplateMetadata();
  const tagsForm = renderTags();

  const contentMetadataForm = (
    <ColumnLayout columns={2} borders="vertical">
      {contentTypeMetadataForm}
      {templateMetadataForm}
      {tagsForm}
    </ColumnLayout>
  );
  return contentMetadataForm;
};

export default ContentMetadataForm;
